import { GrafikCercevesi } from "./GrafikCercevesi";
import { GrafikAciklamasi, GrafikEkseni, HedefCizgisi, NoktaIsareti } from "./grafik-svg";
import {
  gorunenEtiketler,
  hedefDegeri,
  kisalt,
  noktaEtiketi,
  olculen,
  sayiMetni,
  seriRengi,
  xKonumu,
  yOlcegi,
  zamanOzeti,
  zamanSirasi,
} from "./hesap";
import type { ZamanGrafigiProps } from "./tipler";

export interface CizgiGrafikProps extends ZamanGrafigiProps {
  /** Eksenleri gizler; küçük satır içi sunum için KucukCizgi kullanılır. */
  eksensiz?: boolean;
}

export function CizgiGrafik({
  baslik,
  noktalar: hamNoktalar,
  seriler,
  hedef,
  ozet,
  birim = "",
  bosMesaji,
  eksensiz = false,
}: CizgiGrafikProps) {
  const noktalar = zamanSirasi(hamNoktalar);
  // F-EXM-05/09: net negatif olabilir; onu sıfıra kırpmak sonucu değiştirir.
  const degerler = noktalar.map((nokta) =>
    seriler.map((seri) => olculen(nokta.degerler[seri.anahtar], true)),
  );
  const hedefler = noktalar.map((nokta) => hedefDegeri(nokta, hedef, true));
  const olcek = yOlcegi(
    [
      ...degerler.flat().filter((deger): deger is number => deger !== null),
      ...hedefler.filter((deger): deger is number => deger !== null),
    ],
    eksensiz ? 10 : 20,
    eksensiz ? 50 : 172,
  );
  const x = xKonumu(noktalar, eksensiz ? 8 : 52, eksensiz ? 172 : 348);
  const hedefVar = hedefler.some((deger) => deger !== null);
  const ozetMetni = ozet?.trim() || zamanOzeti(baslik, noktalar, seriler, birim, hedef, true);
  const gorunenler = gorunenEtiketler(noktalar.length, x);
  return (
    <GrafikCercevesi
      baslik={baslik}
      ozet={ozetMetni}
      bos={!degerler.some((satir) => satir.some((deger) => deger !== null))}
      bosMesaji={bosMesaji}
      kucuk={eksensiz}
      basliklar={[
        ...seriler.map((seri, sira) => `${sira + 1}. ${seri.etiket}`),
        ...(hedefVar ? ["Hedef"] : []),
      ]}
      satirlar={noktalar.map((nokta, sira) => ({
        anahtar: String(sira),
        etiket: noktaEtiketi(nokta, sira),
        hucreler: [
          ...(degerler[sira] ?? []).map((deger) => sayiMetni(deger, birim)),
          ...(hedefVar ? [sayiMetni(hedefler[sira], birim)] : []),
        ],
      }))}
      aciklama={
        eksensiz ? undefined : (
          <GrafikAciklamasi seriler={seriler} hedefVar={hedefVar} birim={birim} />
        )
      }
    >
      <svg
        role="img"
        aria-label={ozetMetni}
        viewBox={eksensiz ? "0 0 180 60" : "0 0 360 220"}
        width="100%"
        // 480 üstü: viewBox oranla büyüyor, 1440'ta yazılar ~3 kat çıkıyordu (ölçüldü, dalga 3).
        style={{ display: "block", height: "auto", maxWidth: eksensiz ? 180 : 480 }}
      >
        <title>{ozetMetni}</title>
        {!eksensiz && (
          <GrafikEkseni enAz={olcek.enAz} enCok={olcek.enCok} konum={olcek.konum} birim={birim}>
            {noktalar.map((nokta, sira) =>
              gorunenler.has(sira) ? (
                <text
                  key={sira}
                  x={x(sira)}
                  y="196"
                  // Son etiket sağ kenarda: ortalanınca viewBox dışına taşıp kırpılıyordu.
                  textAnchor={sira > 0 && sira === noktalar.length - 1 ? "end" : "middle"}
                >
                  <title>{noktaEtiketi(nokta, sira)}</title>
                  {kisalt(noktaEtiketi(nokta, sira), 8)}
                </text>
              ) : null,
            )}
          </GrafikEkseni>
        )}
        {seriler.map((seri, seriSirasi) => {
          let devam = false;
          const yol = degerler
            .map((satir, sira) => {
              const deger = satir[seriSirasi];
              // F-ANL-08b: eksik değer iki ölçümü birbirine bağlayan çizgi üretmemeli.
              if (deger == null) {
                devam = false;
                return "";
              }
              const parca = `${devam ? "L" : "M"} ${x(sira)} ${olcek.konum(deger)}`;
              devam = true;
              return parca;
            })
            .join(" ");
          const renk = seriRengi(seri, seriSirasi);
          return (
            <g key={`${seri.anahtar}-${seriSirasi}`}>
              <path
                data-testid="seri-cizgisi"
                d={yol}
                stroke={renk}
                strokeWidth="2"
                strokeDasharray={seriSirasi % 2 === 1 ? "4 3" : undefined}
                fill="none"
              />
              {degerler.map((satir, sira) => {
                const deger = satir[seriSirasi];
                return deger == null ? null : (
                  <g data-testid="olcum-noktasi" key={sira}>
                    <title>
                      {noktaEtiketi(noktalar[sira] ?? {}, sira)} — {seri.etiket}:{" "}
                      {sayiMetni(deger, birim)}
                    </title>
                    <NoktaIsareti
                      x={x(sira)}
                      y={olcek.konum(deger)}
                      sira={seriSirasi}
                      renk={renk}
                    />
                  </g>
                );
              })}
            </g>
          );
        })}
        <HedefCizgisi
          hedefler={hedefler}
          x={x}
          y={olcek.konum}
          sol={eksensiz ? 8 : 52}
          sag={eksensiz ? 172 : 348}
          birim={birim}
        />
      </svg>
    </GrafikCercevesi>
  );
}
